// qchat_fe/src/Component/SideData/Overview/AiAgentWorkFlow/OAuthFlowError.jsx
//
// Route component for /oauth/error.
//
// Mounted as a plain <Route> in App.js, NOT a <PrivateRoute>. Same
// reasoning as OAuthFlowSuccess:
//
//   • The backend's /oauth/callback endpoint is unauthenticated
//     (Google is the caller), so gating the frontend tail of the
//     flow behind auth would be inconsistent.
//   • The page reads only URL query params and sessionStorage. It
//     makes zero authenticated backend calls.
//   • If the tenant's token expired during the flow, or if their
//     browser partitioned localStorage across the cross-site
//     navigation to accounts.google.com, a PrivateRoute guard would
//     bounce them to /login and hide the failure explanation — the
//     exact moment when a clear error message matters most.
//
// Nothing on this page is sensitive. The error `reason` and
// `description` come from Google's own redirect and are already in
// the address bar; the "Back to Connections" button lands on the
// admin route, which is a PrivateRoute and will redirect to /login
// for an unauthenticated visitor.
//
// The backend's /oauth/callback handler redirects here whenever the
// consent flow fails, with two query params:
//
//   reason=<short machine-readable code>          e.g. access_denied
//   description=<provider's own message, capped>  e.g. "The user denied..."
//
// We map the known `reason` values to friendly explanations. Unknown
// reasons show the raw string plus the provider description, so a
// failure we haven't accounted for still surfaces something
// actionable.

import React, { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { FaExclamationTriangle } from "react-icons/fa";

// ---------------------------------------------------------------------------
// Reason → friendly text. Keep the keys lowercase; the backend sends
// Google's own values verbatim (RFC 6749 §4.1.2.1 error codes).
// ---------------------------------------------------------------------------
const REASON_COPY = {
  access_denied: {
    title: "Authorization was declined",
    body:
      "You clicked Cancel on the consent screen, or declined one of the " +
      "requested permissions. Nothing was changed. You can try again " +
      "whenever you're ready.",
    recoverable: true,
  },
  invalid_request: {
    title: "The authorization request was rejected",
    body:
      "Google rejected the request. This usually means the OAuth client " +
      "in the Google Cloud Console is misconfigured, or the redirect URL " +
      "doesn't match what's registered. If this keeps happening, contact " +
      "support with the description below.",
    recoverable: false,
  },
  unauthorized_client: {
    title: "This app isn't authorized for that account",
    body:
      "The Google account you signed in with isn't allowed to use this " +
      "OAuth client. If you're testing, make sure the account is added to " +
      "the test users list. If you're a customer, contact support.",
    recoverable: false,
  },
  invalid_scope: {
    title: "A requested permission was rejected",
    body:
      "The scope bundle Sageion asked for doesn't match what's configured " +
      "on the OAuth client. Contact support with the description below.",
    recoverable: false,
  },
  server_error: {
    title: "Google had a server error",
    body:
      "This is on Google's side, not yours. Wait a moment and try again.",
    recoverable: true,
  },
  temporarily_unavailable: {
    title: "Google is temporarily unavailable",
    body:
      "This is on Google's side, not yours. Wait a moment and try again.",
    recoverable: true,
  },
};

const DEFAULT_COPY = {
  title: "The connection couldn't be completed",
  body:
    "Something went wrong during the authorization flow. Try again, and if " +
    "the problem persists, share the description below with support.",
  recoverable: true,
};

const errorStyles = {
  page: {
    minHeight: "70vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "32px",
  },
  card: {
    maxWidth: 480,
    width: "100%",
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "32px 28px",
    textAlign: "center",
    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.04)",
  },
  icon: {
    color: "#dd6b20",
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: 600,
    color: "#1a202c",
    margin: "0 0 8px 0",
  },
  body: {
    fontSize: 14,
    color: "#4a5568",
    lineHeight: 1.6,
    margin: "0 0 16px 0",
    textAlign: "left",
  },
  reasonChip: {
    display: "inline-block",
    fontSize: 11,
    fontFamily: "monospace",
    color: "#92400e",
    background: "#fef3c7",
    border: "1px solid #fcd34d",
    padding: "3px 10px",
    borderRadius: 10,
    marginBottom: 16,
  },
  detailBox: {
    margin: "16px 0 20px 0",
    padding: "12px 14px",
    background: "#f7fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    textAlign: "left",
  },
  detailLabel: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    color: "#718096",
    marginBottom: 6,
  },
  detailValue: {
    fontSize: 12,
    color: "#2d3748",
    lineHeight: 1.55,
    wordBreak: "break-word",
    fontFamily: "monospace",
  },
  buttonRow: {
    display: "flex",
    gap: 10,
    justifyContent: "center",
    marginTop: 4,
  },
  primaryButton: {
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    color: "#ffffff",
    background: "#4299e1",
    border: "none",
    borderRadius: 6,
    cursor: "pointer",
  },
  secondaryButton: {
    padding: "10px 24px",
    fontSize: 14,
    fontWeight: 600,
    color: "#4a5568",
    background: "#edf2f7",
    border: "1px solid #e2e8f0",
    borderRadius: 6,
    cursor: "pointer",
  },
};

export default function OAuthFlowError() {
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const reason = params.get("reason") || "unknown_error";
  const description = params.get("description") || "";

  // Recover the app the user came from — same mechanism as the
  // success page. Written by the Connections card before it
  // navigated away to /oauth/start.
  //
  // Best-effort: if sessionStorage was wiped or the key was never
  // written, goBack falls through to "/" and the PrivateRoute guard
  // there handles the rest.
  const [returnApp, setReturnApp] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("oauth_return_to");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed?.region && parsed?.appName) {
          setReturnApp(parsed);
        }
      }
    } catch (_) {
      // ignore — will fall back to home
    }
  }, []);

  const copy = REASON_COPY[reason] || DEFAULT_COPY;

  const goBack = () => {
    if (returnApp?.region && returnApp?.appName) {
      // Send the user to the app's Connections tab. Same route
      // shape the success page uses — AppDetailPage reads
      // state.activeItemId on mount to pick the initial tab.
      history.push(
        `/${returnApp.region}/${returnApp.appName}/app-detail`,
        { activeItemId: "Connections" },
      );
    } else {
      // Fallback. "/" is a PrivateRoute, so an unauthenticated
      // visitor is bounced to /login — which is fine, they'll
      // land on the app list after signing in.
      history.push("/");
    }
  };

  const tryAgain = () => {
    // We don't re-initiate the flow from here, because we don't know
    // which provider the user was trying to connect (the error
    // redirect doesn't carry that). Instead we send them back to the
    // Connections card, where the Connect button is one click away
    // and the flow can be restarted with the provider they meant to
    // pick. This is intentionally the same destination as goBack.
    goBack();
  };

  return (
    <div style={errorStyles.page}>
      <div style={errorStyles.card}>
        <FaExclamationTriangle size={44} style={errorStyles.icon} />

        <h1 style={errorStyles.title}>{copy.title}</h1>

        <span style={errorStyles.reasonChip}>{reason}</span>

        <p style={errorStyles.body}>{copy.body}</p>

        {description && (
          <div style={errorStyles.detailBox}>
            <div style={errorStyles.detailLabel}>What the provider said</div>
            <div style={errorStyles.detailValue}>{description}</div>
          </div>
        )}

        <div style={errorStyles.buttonRow}>
          {copy.recoverable && (
            <button
              type="button"
              style={errorStyles.primaryButton}
              onClick={tryAgain}
            >
              Try again
            </button>
          )}
          <button
            type="button"
            style={errorStyles.secondaryButton}
            onClick={goBack}
          >
            Back to Connections
          </button>
        </div>
      </div>
    </div>
  );
}